import * as Sentry from '@sentry/react';
import { Descope } from '@descope/react-sdk/flows';
import { useCallback, useEffect, useRef } from 'react';
import { useDescopeSdk } from '../../../../hooks/use-descope-sdk.ts';
import { DescopeErrorBoundary } from '../../../../contexts/descope-auth/DescopeErrorBoundary';

const SIGN_IN_FLOW_ID = 'sign-in';
const SIGN_IN_INTERACTION_ID = 'sign_in_interaction';

type NextFn = (interactionId: string, form: Record<string, unknown>) => Promise<unknown>;

type DescopeSignInProps = {
  credentials: { email: string; password: string } | null;
};

function DescopeSignInFlow({ credentials }: DescopeSignInProps) {
  const nextRef = useRef<NextFn | null>(null);
  const pendingRef = useRef<DescopeSignInProps['credentials']>(null);
  const submittedRef = useRef(false);

  const submit = useCallback(async (next: NextFn, credentials: { email: string; password: string }) => {
    if (submittedRef.current) return;
    submittedRef.current = true;

    try {
      await next(SIGN_IN_INTERACTION_ID, { email: credentials.email, password: credentials.password });
    } catch (error) {
      Sentry.captureException(error, { extra: { component: 'DescopeSignIn', action: 'submit' } });
    }
  }, []);

  const handleScreenUpdate = useCallback(
    (_screenName: string, _state: Record<string, unknown>, next: NextFn, _ref: HTMLElement) => {
      nextRef.current = next;

      if (pendingRef.current) {
        void submit(next, pendingRef.current);
        pendingRef.current = null;
      }

      return false;
    },
    [submit],
  );

  const handleError = useCallback((event: CustomEvent) => {
    Sentry.captureMessage('Descope sign-in flow error', {
      level: 'warning',
      extra: { detail: event.detail, component: 'DescopeSignIn' },
    });
  }, []);

  useEffect(() => {
    if (!credentials) return;
    submittedRef.current = false;

    if (nextRef.current) {
      void submit(nextRef.current, credentials);
    } else {
      pendingRef.current = credentials;
    }
  }, [credentials, submit]);

  return (
    <Descope
      flowId={SIGN_IN_FLOW_ID}
      onScreenUpdate={handleScreenUpdate}
      onError={handleError}
    />
  );
}

export function DescopeSignIn({ credentials }: DescopeSignInProps) {
  const descope = useDescopeSdk();

  if (!descope) return null;

  return (
    <DescopeErrorBoundary>
      <div style={{ display: 'none' }}>
        <DescopeSignInFlow credentials={credentials} />
      </div>
    </DescopeErrorBoundary>
  );
}
